import { Edit2, MoreHorizontal, Trash2 } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

import { Icon } from "@/components/atoms/Icon";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 * Props for BoardOptionsMenu component
 */
export interface BoardOptionsMenuProps {
  /** Callback when user selects rename board option */
  onRenameBoard: () => void;
  /** Callback when user selects delete board option */
  onDeleteBoard: () => void;
  /** Whether delete option should be enabled (false for last board) */
  canDelete?: boolean;
}

/**
 * BoardOptionsMenu Component - board management options dropdown.
 *
 * Provides a "..." trigger with board management options:
 * - Rename Board: Allows editing the board name
 * - Delete Board: Removes the board (disabled for last remaining board)
 *
 * SE-5570: renders through the shared `DesktopDropdownMenu` molecule
 * (FixedPositionDropdownShell) instead of the retired gluestack Menu atom —
 * the container owns the open state (controlled), and the trigger icon uses
 * the Icon atom with semantic tokens (drops the raw lucide `@ts-ignore` and
 * the useKanbanColors hex).
 * @param root0 - The component props
 * @param root0.onRenameBoard - Callback invoked when user selects the rename board option
 * @param root0.onDeleteBoard - Callback invoked when user selects the delete board option
 * @param root0.canDelete - Boolean flag indicating if board deletion is allowed (false for last remaining board)
 */
const BoardOptionsMenuContainer = ({
  onRenameBoard,
  onDeleteBoard,
  canDelete = true,
}: BoardOptionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
  const onClose = useCallback(() => setIsOpen(false), []);

  const items = useMemo<DesktopDropdownMenuItem[]>(
    () => [
      {
        id: "rename-board",
        label: "Rename Board",
        icon: Edit2,
        onPress: onRenameBoard,
      },
      {
        id: "delete-board",
        label: "Delete Board",
        icon: Trash2,
        onPress: onDeleteBoard,
        destructive: canDelete,
        disabled: !canDelete,
      },
    ],
    [onRenameBoard, onDeleteBoard, canDelete]
  );

  return (
    <DesktopDropdownMenu
      isOpen={isOpen}
      onClose={onClose}
      onToggle={onToggle}
      triggerTestID={TEST_ID_PLAYER_KANBAN.BOARD_OPTIONS_MENU}
      triggerAccessibilityLabel="Board options"
      triggerClassName="h-10 w-9 items-center justify-center rounded-t-sm border-b-0 bg-surface-base p-2"
      triggerContent={
        <Icon
          as={MoreHorizontal}
          size="sm"
          className="text-content-secondary"
        />
      }
      items={items}
    />
  );
};

export default BoardOptionsMenuContainer;
